import React, { ReactNode, useMemo } from 'react';
import { DatePicker, Form, FormControlProps, Text } from 'rsuite';
import dayjs from 'dayjs';

type AnnotatedDatePickerProps = {
  annotation?: () => string | undefined;
  errorMessage?: string;
  format?: string;
  hasError?: boolean;
  label: string;
  required?: boolean;
  subtitle?: () => ReactNode;
} & Omit<
  FormControlProps<string, string>,
  'accepter' | 'onSelect' | 'format' | 'oneTap' | 'editable'
>;

const AnnotatedDatePicker = ({
  annotation = () => undefined,
  errorMessage,
  format = 'YYYY-MM-DD',
  hasError,
  label,
  name,
  onChange,
  required,
  subtitle = () => undefined,
  value,
  ...rest
}: AnnotatedDatePickerProps) => {
  const formattedValue = useMemo(() => {
    const d = dayjs(value);
    if (d.isValid()) {
      return d.toDate();
    }
    return undefined;
  }, [value]);

  const onInternalChange = (date: Date | null, ev: React.SyntheticEvent) => {
    const dayjsDate = dayjs(date);
    if (onChange) {
      if (dayjsDate.isValid()) {
        const formatted = dayjsDate.format(format);
        onChange(formatted, ev);
      } else {
        onChange('', ev);
      }
    }
  };

  const errorId = hasError && name ? `${name}-error` : undefined;

  return (
    <Form.Group controlId={name}>
      <Form.ControlLabel>
        <Text
          as="h5"
          className="text-bold"
        >
          {label}
          {required && ' *'}
        </Text>
        {subtitle && <Text>{subtitle()}</Text>}
      </Form.ControlLabel>
      <Form.Control
        accepter={DatePicker}
        key={value ? 'date-picker' : 'empty-date-picker'}
        name={name}
        onChange={onInternalChange}
        value={formattedValue}
        aria-invalid={hasError || undefined}
        aria-describedby={errorId}
        {...rest}
      />
      {hasError && errorMessage && (
        <span
          id={errorId}
          className="text-error"
        >
          {errorMessage}
        </span>
      )}
      {annotation()}
    </Form.Group>
  );
};

export default AnnotatedDatePicker;
